import { motion, AnimatePresence, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { track } from "@/lib/metaPixel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VOLUME_OPTIONS = [
  "Less than 1M",
  "$1M – $5M",
  "$5M – $15M",
  "$15M – $30M",
  "$30M+",
];

const H1_LINES = [
  { text: "READY TO OWN", color: "#F0ECE4" },
  { text: "YOUR PIPELINE?", color: "#C9A84C" },
];

export default function CtaSection() {
  const headRef = useRef(null);
  const headIn = useInView(headRef, { once: true, amount: 0.1 });
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    state: "",
    current_lead_source: "",
    monthly_volume: "",
    message: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const bookHref = () => {
    const q = new URLSearchParams({
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone: form.phone,
    });
    return `/book?${q.toString()}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/wam-application`, form);
      // Meta Pixel: application submitted (soft lead — separate from booked-call Lead on /thank-you)
      track("Lead", {
        content_name: "WAM Application Submitted",
        content_category: "application_form",
      });
      setSuccess(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email dallas@smallbusinessautomator.com");
    } finally {
      setSubmitting(false);
    }
  };

  const goToBook = () => navigate(bookHref());

  return (
    <section
      id="apply"
      className="relative overflow-hidden py-32 md:py-48"
      data-testid="cta-section"
    >
      {/* Gold radial glow */}
      <div
        aria-hidden
        className="absolute -left-40 top-1/3 h-[600px] w-[600px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(closest-side, rgba(201,168,76,0.14), transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-[1200px] px-6 md:px-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="mb-10 flex items-center justify-center gap-4"
        >
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#F0ECE4]/40">
            [009] —
          </span>
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#C9A84C]">
            THE CLOSE
          </span>
        </motion.div>

        <h2
          ref={headRef}
          className="font-display"
          style={{ fontSize: "clamp(48px, 9vw, 140px)", lineHeight: 0.9, letterSpacing: "-0.04em" }}
        >
          {H1_LINES.map((l, i) => (
            <div key={l.text} className="overflow-hidden">
              <motion.div
                initial={{ y: "110%" }}
                animate={headIn ? { y: "0%" } : { y: "110%" }}
                transition={{ duration: 1.1, delay: i * 0.15, ease: [0.85, 0, 0.15, 1] }}
                style={{ color: l.color }}
              >
                {l.text}
              </motion.div>
            </div>
          ))}
        </h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.55 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mx-auto mt-10 max-w-[600px] font-body text-[16px] leading-[1.75] text-[#F0ECE4]"
        >
          Book a free 15-minute discovery call. We&rsquo;ll confirm your niche, walk
          through the system, and tell you honestly if WAM is right for you.
        </motion.p>

        <div className="mt-12 flex flex-col items-center">
          <button
            onClick={goToBook}
            className="wam-btn-primary w-full max-w-[440px] text-[13px] py-5"
            data-testid="cta-book-call-btn"
          >
            Book Your Discovery Call →
          </button>
          <div className="mt-4 font-mono text-[10px] tracking-[0.28em] text-[#F0ECE4]/25">
            15 MINUTES · NO PITCH DECK · JUST A REAL CONVERSATION
          </div>

          {!showForm && !success && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-8 font-mono text-[11px] tracking-[0.24em] text-[#F0ECE4]/50 hover:text-[#C9A84C] transition-colors"
              data-testid="cta-toggle-form"
            >
              Or send an application first ↓
            </button>
          )}
        </div>

        {/* Form */}
        <AnimatePresence>
          {showForm && !success && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              onSubmit={submit}
              className="mx-auto mt-16 max-w-[720px] text-left overflow-hidden"
              data-testid="wam-application-form"
            >
              <div className="border border-white/5 bg-white/[0.015] p-8 md:p-12">
                <div className="font-mono text-[10px] tracking-[0.28em] text-[#C9A84C] mb-6">
                  APPLICATION FORM
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <Field label="FIRST NAME" required value={form.first_name} onChange={update("first_name")} testid="form-first-name" />
                  <Field label="LAST NAME" required value={form.last_name} onChange={update("last_name")} testid="form-last-name" />
                  <Field label="EMAIL" type="email" required value={form.email} onChange={update("email")} testid="form-email" />
                  <Field label="PHONE" type="tel" required value={form.phone} onChange={update("phone")} testid="form-phone" />
                  <Field label="STATE LICENSED IN" required value={form.state} onChange={update("state")} testid="form-state" />
                  <Field label="CURRENT LEAD SOURCE" value={form.current_lead_source} onChange={update("current_lead_source")} testid="form-lead-source" />
                  <div className="md:col-span-2">
                    <label className="block font-mono text-[9px] tracking-[0.28em] text-[#F0ECE4]/50 mb-2">
                      MONTHLY LOAN VOLUME
                    </label>
                    <select
                      value={form.monthly_volume}
                      onChange={update("monthly_volume")}
                      data-testid="form-volume"
                      className="w-full bg-transparent border-b border-white/20 py-3 font-body text-[15px] text-[#F0ECE4] focus:border-[#C9A84C] focus:outline-none transition-colors"
                    >
                      <option value="" className="bg-[#070709]">Select…</option>
                      {VOLUME_OPTIONS.map((v) => (
                        <option key={v} value={v} className="bg-[#070709]">{v}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-mono text-[9px] tracking-[0.28em] text-[#F0ECE4]/50 mb-2">
                      MESSAGE
                    </label>
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={update("message")}
                      data-testid="form-message"
                      className="w-full bg-transparent border-b border-white/20 py-3 font-body text-[15px] text-[#F0ECE4] focus:border-[#C9A84C] focus:outline-none transition-colors resize-none"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="wam-btn-primary mt-10 w-full text-[12px] py-4 disabled:opacity-60"
                  data-testid="form-submit"
                >
                  {submitting ? "Submitting…" : "Submit Application →"}
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Success */}
        <AnimatePresence>
          {success && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto mt-16 max-w-[640px] border border-[#C9A84C] bg-[#C9A84C]/[0.06] p-10 text-center"
              data-testid="form-success-card"
            >
              <div className="font-display text-[52px] leading-none text-[#C9A84C]">✓</div>
              <div className="mt-4 font-display text-[22px] tracking-[-0.02em]">
                APPLICATION RECEIVED
              </div>
              <p className="mt-3 font-body text-[14px] leading-[1.7] text-[#F0ECE4]/70">
                Locked in. Next step — pick a time for your 15-minute discovery
                call. Your details are pre-filled for you.
              </p>
              <button
                onClick={goToBook}
                data-testid="success-book-cta"
                className="wam-btn-primary mt-8 w-full text-[12px] py-4"
              >
                Continue to Booking →
              </button>
              <div className="mt-4 font-mono text-[9px] tracking-[0.28em] text-[#F0ECE4]/35">
                DALLAS WILL ALSO EMAIL YOU WITHIN 24 HOURS
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="block font-mono text-[9px] tracking-[0.28em] text-[#F0ECE4]/50 mb-2">
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-b border-white/20 py-3 font-body text-[15px] text-[#F0ECE4] focus:border-[#C9A84C] focus:outline-none transition-colors"
      />
    </div>
  );
}
